import React from 'react';
import ReactDOM from 'react-dom';

type Props = {
  anchorRef: React.RefObject<HTMLElement>;
  open: boolean;
  onClose?: () => void;
  children: React.ReactNode;
  minWidth?: number;
  align?: 'left' | 'right';
  offsetX?: number;
  maxWidth?: number;
};

export default function DropdownMenuPortal({
  anchorRef,
  open,
  onClose,
  children,
  minWidth = 140,
  maxWidth,
  align = 'left',
  offsetX = 0,
}: Props) {
  const [pos, setPos] = React.useState<{ top: number; left: number; width: number } | null>(null);

  const updatePos = React.useCallback(() => {
    if (!anchorRef?.current) return;
    const r = (anchorRef.current as HTMLElement).getBoundingClientRect();
    setPos({ top: r.bottom + window.scrollY + 4, left: r.left + window.scrollX, width: r.width });
  }, [anchorRef]);

  React.useEffect(() => {
    if (!open) return;
    updatePos();
    const onDocClick = (e: MouseEvent) => {
      if (!anchorRef.current) return;
      const target = e.target as HTMLElement;
      if (
        !anchorRef.current.contains(target) &&
        !(target.closest && target.closest('.qt-inline-menu'))
      ) {
        onClose?.();
      }
    };
    const onResize = () => updatePos();
    window.addEventListener('mousedown', onDocClick, true);
    window.addEventListener('scroll', onResize, true);
    window.addEventListener('resize', onResize, true);
    return () => {
      window.removeEventListener('mousedown', onDocClick, true);
      window.removeEventListener('scroll', onResize, true);
      window.removeEventListener('resize', onResize, true);
    };
  }, [open, anchorRef, onClose, updatePos]);

  if (!open || !pos) return null;
  const width = Math.max(minWidth, pos.width || 0);
  const left = (align === 'right' ? pos.left + pos.width - width : pos.left) + offsetX;
  return ReactDOM.createPortal(
    <div
      className="qt-inline-menu dropdown-menu show"
      style={{
        position: 'absolute',
        top: pos.top,
        left,
        minWidth: width,
        maxWidth: maxWidth || 350,
        padding: '6px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
        zIndex: 3000,
      }}
    >
      {children}
    </div>,
    document.body
  );
}
